import type { BulkValidatedRow, BulkWorkflow } from './bulkOperations';

export const bulkReferenceFields = ['personId', 'workerId', 'companyId', 'organizationId', 'locationId', 'jobProfileId', 'jobTypeId', 'eventId', 'entryId', 'timeOffTypeId', 'entitlementId', 'locationCode', 'jobFamilyCode'] as const;
export type BulkReferenceField = typeof bulkReferenceFields[number];
export type BulkReferenceOption = { id: string; label: string; description?: string; searchText?: string; aliases?: string[] };
export type BulkReferencePort = { search(field: BulkReferenceField, query: string, values: Record<string, string>): Promise<BulkReferenceOption[]>; reset?(): void };
export const isBulkReferenceField = (key: string): key is BulkReferenceField => bulkReferenceFields.some((field) => field === key);

/** ID 또는 이름·별칭의 정확 일치 후보만 반환합니다. 중복 이름을 임의로 고르지 않습니다. */
export function matchBulkReference(input: string, options: readonly BulkReferenceOption[]): BulkReferenceOption[] {
  const exact = options.find((option) => option.id === input);
  if (exact) return [exact];
  const normalize = (value: string) => value.trim().toLocaleLowerCase();
  return options.filter((option) => [option.label, ...(option.aliases ?? [])].some((alias) => normalize(alias) === normalize(input)));
}

export function bulkReferenceContext(field: BulkReferenceField, values: Record<string, string>) {
  return JSON.stringify([values.effectiveDate || values.hireDate || values.startDate || values.effectiveOn || '', field === 'entitlementId' || field === 'entryId' ? values.workerId : '', field === 'jobTypeId' ? values.workerType || 'EMPLOYEE' : '']);
}

/** 이름은 유일한 정확 일치일 때만 연결합니다. 부분 일치와 동명이인은 사용자가 선택합니다. */
export async function resolveBulkReferences(workflow: BulkWorkflow, source: BulkValidatedRow[], port: BulkReferencePort): Promise<BulkValidatedRow[]> {
  const cache = new Map<string, Promise<BulkReferenceOption[]>>();
  const rows = source.map((row) => ({ ...row, values: { ...row.values }, errors: [...row.errors], referenceMatches: { ...row.referenceMatches } }));
  // 구성원을 먼저 연결해야 잔액을 해당 구성원 범위에서만 조회할 수 있습니다.
  const order = (key: string) => key === 'workerId' ? 0 : key === 'entitlementId' || key === 'entryId' ? 2 : 1;
  const columns = workflow.columns.filter((column) => isBulkReferenceField(column.key)).sort((a, b) => order(a.key) - order(b.key));
  for (const column of columns) {
    const field = column.key as BulkReferenceField;
    let cursor = 0;
    await Promise.all(Array.from({ length: Math.min(4, rows.length) }, async () => {
      while (cursor < rows.length) {
        const row = rows[cursor++];
        const input = row.values[field]?.trim() ?? '';
        const preparedMatch = row.referenceMatches[field];
        if (preparedMatch?.status === 'MATCHED' && preparedMatch.matched?.id === input) continue;
        if (!input) {
          row.referenceMatches[field] = { input, status: 'MISSING' };
          continue;
        }
        if (field === 'entitlementId' && row.referenceMatches.workerId?.status !== 'MATCHED') {
          row.referenceMatches[field] = { input, status: 'NOT_FOUND' };
          row.errors.push('휴가 잔액을 연결하기 전에 구성원을 선택해 주세요.');
          continue;
        }
        try {
          const key = JSON.stringify([field, input, bulkReferenceContext(field, row.values)]);
          let request = cache.get(key);
          if (!request) { request = port.search(field, input, row.values); cache.set(key, request); }
          const options = await request;
          const matches = matchBulkReference(input, options);
          if (matches.length === 1) {
            const option = matches[0];
            row.values[field] = option.id;
            row.referenceMatches[field] = { input, status: 'MATCHED', matched: { id: option.id, descriptor: [option.label, option.description].filter(Boolean).join(' · ') } };
          } else {
            row.referenceMatches[field] = { input, status: matches.length > 1 ? 'AMBIGUOUS' : 'NOT_FOUND', candidateCount: matches.length };
            row.errors.push(`${column.label}: ${matches.length > 1 ? '이름이 중복됩니다.' : '연결할 대상을 확인해 주세요.'} 검색 결과에서 선택해 주세요.`);
          }
        } catch (error) {
          row.referenceMatches[field] = { input, status: 'NOT_FOUND' };
          row.errors.push(`${column.label}: ${error instanceof Error ? error.message : '조회하지 못했습니다. 다시 검색해 주세요.'}`);
        }
      }
    }));
  }
  return rows;
}

export function validateBulkValues(workflow: BulkWorkflow, rows: BulkValidatedRow[]) {
  return rows.map((source) => {
    const row = { ...source, values: { ...source.values } };
    for (const column of workflow.columns) if (column.parse) row.values[column.key] = column.parse(row.values[column.key] ?? '');
    return { ...row, errors: [...row.errors, ...workflow.columns.flatMap((column) => {
      const value = row.values[column.key] ?? '';
      if (column.required && !value) return [`${column.label} 값이 필요합니다.`];
      const error = value && column.validate?.(value, row.values);
      return error ? [error] : [];
    }), ...(workflow.validateRow?.(row.values) ?? [])] };
  });
}

/** 원본 셀과 대상·날짜 문맥이 그대로인 연결만 다시 사용하고 실제 유효성은 재검증합니다. */
export function preserveBulkReferenceSelections(source: Record<string, string>[], previousSource: Record<string, string>[], previousRows: BulkValidatedRow[]) {
  const identityFields = ['systemId', 'workerNumber', 'legalName', 'legalFamilyName', 'legalGivenName', 'personId', 'workerId'];
  return source.map((row, index) => {
    const previous = previousSource[index];
    const resolved = previousRows[index];
    if (!previous || !resolved || identityFields.some((field) => (row[field] ?? '') !== (previous[field] ?? ''))) return row;
    const values = { ...row };
    for (const [field, match] of Object.entries(resolved.referenceMatches ?? {})) {
      if (!isBulkReferenceField(field) || match.status !== 'MATCHED' || !match.matched) continue;
      if ((row[field] ?? '') === (previous[field] ?? '') && bulkReferenceContext(field, row) === bulkReferenceContext(field, previous)) values[field] = match.matched.id;
    }
    return values;
  });
}
